import React, { Suspense, lazy } from 'react';
import { installGlobalClickTracking } from './utils/track';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import WMSLayout from './layouts/WMSLayout';
import AuthLayout from './components/layout/AuthLayout';
import ProtectedRoute from './components/common/ProtectedRoute';
import KaiWidget from './components/common/KaiWidget';
import RequireMandatoryConsent from './features/auth/RequireMandatoryConsent';
import ConsentScreen from './features/auth/ConsentScreen';
import RequireKyc from './features/onboarding/RequireKyc';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import { ROLES } from './utils/constants';

// Public info / legal pages — wired into the marketing footer.
const HowItWorksPage = lazy(() => import('./features/info/HowItWorks'));
const TermsPage = lazy(() => import('./features/info/Terms'));
const PrivacyPage = lazy(() => import('./features/info/Privacy'));
const RefundPolicyPage = lazy(() => import('./features/info/RefundPolicy'));
const GrievancePage = lazy(() => import('./features/info/Grievance'));
const AccountDeletionPage = lazy(() => import('./features/info/AccountDeletion'));
const AboutPage = lazy(() => import('./features/info/About'));
const FAQPage = lazy(() => import('./features/info/FAQ'));
const ContactPage = lazy(() => import('./features/info/Contact'));
const PricingPage = lazy(() => import('./features/info/Pricing'));
const CompliancePage = lazy(() => import('./features/info/compliance/CompliancePage'));
const IntegrationsPage = lazy(() => import('./features/info/integrations/IntegrationsPage'));

// Auth pages
const Login = lazy(() => import('./features/auth/Login'));
const Register = lazy(() => import('./features/auth/Register'));
const ForgotPassword = lazy(() => import('./features/auth/ForgotPassword'));
const KYCUpload = lazy(() => import('./features/auth/KYCUpload'));

// Dashboard
const DashboardRouter = lazy(() => import('./features/dashboard/DashboardRouter'));

// Warehouse
const ListingList = lazy(() => import('./features/warehouse/ListingList'));
const ListingWizard = lazy(() => import('./features/warehouse/ListingWizard'));
const ListingDetail = lazy(() => import('./features/warehouse/ListingDetail'));
const ListingApproval = lazy(() => import('./features/warehouse/ListingApproval'));
const AvailabilityCalendar = lazy(() => import('./features/warehouse/AvailabilityCalendar'));
const WarehouseOnboarding = lazy(() => import('./features/warehouse/WarehouseOnboarding'));
const ShelfConfiguration = lazy(() => import('./features/warehouse/ShelfConfiguration'));
const WarehouseMap = lazy(() => import('./features/warehouse/WarehouseMap'));

// Marketplace
const SearchPage = lazy(() => import('./features/marketplace/SearchPage'));
const FindWarehouseWizard = lazy(() => import('./features/marketplace/FindWarehouseWizard'));
const CostCalculator = lazy(() => import('./features/marketplace/CostCalculator'));
const CityLanding = lazy(() => import('./features/marketplace/CityLanding'));
const WarehouseRentIndex = lazy(() => import('./features/marketplace/WarehouseRentIndex'));
const CityHub = lazy(() => import('./features/marketplace/CityLanding').then((m) => ({ default: m.CityHub })));
const BlogList = lazy(() => import('./features/blog/BlogList'));
const BlogPost = lazy(() => import('./features/blog/BlogPost'));
const BlogManager = lazy(() => import('./features/blog/BlogManager'));
const ListingDetailPublic = lazy(() => import('./features/marketplace/listing-detail'));
const HostProfilePage = lazy(() => import('./features/marketplace/host-profile/HostProfilePage'));
const BookingFlow = lazy(() => import('./features/marketplace/booking-flow'));
const ComparisonTool = lazy(() => import('./features/marketplace/ComparisonTool'));
const SavedSearches = lazy(() => import('./features/marketplace/SavedSearches'));

// Contracts
const ContractList = lazy(() => import('./features/contracts/ContractList'));
const ContractDetail = lazy(() => import('./features/contracts/ContractDetail'));

// Bookings
const BookingList = lazy(() => import('./features/bookings/BookingList'));
const BookingDetail = lazy(() => import('./features/bookings/BookingDetail'));
const BookingApproval = lazy(() => import('./features/bookings/BookingApproval'));
const ContractViewer = lazy(() => import('./features/bookings/ContractViewer'));

// Inventory
const GRNCreate = lazy(() => import('./features/inventory/GRNCreate'));
const GRNList = lazy(() => import('./features/inventory/GRNList'));
const GRNDetail = lazy(() => import('./features/inventory/GRNDetail'));
const QCInspection = lazy(() => import('./features/inventory/QCInspection'));
const InventoryList = lazy(() => import('./features/inventory/InventoryList'));
const DispatchCreate = lazy(() => import('./features/inventory/DispatchCreate'));
const DispatchList = lazy(() => import('./features/inventory/DispatchList'));
const WarehouseLayout = lazy(() => import('./features/inventory/WarehouseLayout'));
const ReturnsManagement = lazy(() => import('./features/inventory/ReturnsManagement'));
const DamageLossRegister = lazy(() => import('./features/inventory/DamageLossRegister'));
// Phase 7b — tenant approval queue for damage claims. Distinct
// page from the Damage Register so the stock owner has a focused
// inbox (no tabs, no insurance noise) just for accept/reject work.
const DamageApprovals = lazy(() => import('./features/inventory/DamageApprovals'));
// Tenant-aware putaway workbench — visible payoff of the Phase 1+2
// backend work (tenant-aware suggester, override audit, alloc bump).
const PutawayWorkbench = lazy(() => import('./features/operations/putaway/PutawayWorkbench'));

// Billing
const InvoiceList = lazy(() => import('./features/billing/InvoiceList'));
const InvoiceDetail = lazy(() => import('./features/billing/InvoiceDetail'));
const PaymentHistory = lazy(() => import('./features/billing/PaymentHistory'));
const RateCardManagement = lazy(() => import('./features/billing/RateCardManagement'));
const PayoutDashboard = lazy(() => import('./features/billing/PayoutDashboard'));
const HostPayouts = lazy(() => import('./features/finance/HostPayouts'));
const HostBankAccounts = lazy(() => import('./features/finance/HostBankAccounts'));
const MyInvoices = lazy(() => import('./features/finance/MyInvoices'));
const NachMandates = lazy(() => import('./features/billing/NachMandates'));
const ReversalLog = lazy(() => import('./features/finance/ReversalLog'));
const GSTReport = lazy(() => import('./features/billing/GSTReport'));
const GSTR3BReport = lazy(() => import('./features/billing/GSTR3BReport'));
const GSTR9Report = lazy(() => import('./features/billing/GSTR9Report'));
const TaxLedger = lazy(() => import('./features/billing/TaxLedger'));
const CreditLimitManagement = lazy(() => import('./features/billing/credit-limits'));
// Phase 14r — inward ITC surfaces.
const VendorMaster = lazy(() => import('./features/billing/VendorMaster'));
const PurchaseInvoices = lazy(() => import('./features/billing/PurchaseInvoices'));
const Gstr2bReconciliation = lazy(() => import('./features/billing/Gstr2bReconciliation'));
// Phase 14v — compliance ops dashboard.
const ComplianceCalendar = lazy(() => import('./features/billing/ComplianceCalendar'));
// Phase 16 — settlement admin surfaces.
const CommissionRateCards = lazy(() => import('./features/finance/CommissionRateCards'));
const SettlementSettings = lazy(() => import('./features/finance/SettlementSettings'));
const ClawbackLog = lazy(() => import('./features/finance/ClawbackLog'));
const RollingReserveLog = lazy(() => import('./features/finance/RollingReserveLog'));
const HostSettlementStatement = lazy(() => import('./features/finance/HostSettlementStatement'));
const Gstr8Report = lazy(() => import('./features/finance/Gstr8Report'));

// Spaces
const SpaceOverview = lazy(() => import('./features/space/SpaceOverview'));
const ZoneManagement = lazy(() => import('./features/space/ZoneManagement'));
const RackManagement = lazy(() => import('./features/space/RackManagement'));
const AllocationList = lazy(() => import('./features/space/AllocationList'));

// Operations
const TaskBoard = lazy(() => import('./features/operations/TaskBoard'));
const TaskDetail = lazy(() => import('./features/operations/TaskDetail'));
const DockSchedule = lazy(() => import('./features/operations/DockSchedule'));
const OverstayManagement = lazy(() => import('./features/operations/OverstayManagement'));
const InboundOutboundTracker = lazy(() => import('./features/operations/InboundOutboundTracker'));
const PackagingManagement = lazy(() => import('./features/operations/PackagingManagement'));
const WorkforceManagement = lazy(() => import('./features/operations/WorkforceManagement'));
const TeamChat = lazy(() => import('./features/operations/TeamChat'));

// Notifications
const NotificationCenter = lazy(() => import('./features/notifications/NotificationCenter'));
const NotificationPreferences = lazy(() => import('./features/notifications/NotificationPreferences'));
const NotificationTemplates = lazy(() => import('./features/notifications/NotificationTemplates'));

// Reviews
const ReviewForm = lazy(() => import('./features/reviews/ReviewForm'));
const ReviewList = lazy(() => import('./features/reviews/ReviewList'));
const TrustScoreCard = lazy(() => import('./features/reviews/TrustScoreCard'));
const TrustScoreManagement = lazy(() => import('./features/reviews/TrustScoreManagement'));
const MyReviews = lazy(() => import('./features/reviews/MyReviews'));
const MyTrustScore = lazy(() => import('./features/reviews/MyTrustScore'));
const FulfillmentRoles = lazy(() => import('./features/settings/FulfillmentRoles'));

// Disputes
const DisputeCreate = lazy(() => import('./features/disputes/DisputeCreate'));
const DisputeList = lazy(() => import('./features/disputes/DisputeList'));
const DisputeDetail = lazy(() => import('./features/disputes/DisputeDetail'));

// Logistics
const FreightRequestCreate = lazy(() => import('./features/logistics/FreightRequestCreate'));
const FreightRequestList = lazy(() => import('./features/logistics/FreightRequestList'));
const ShipmentTracking = lazy(() => import('./features/logistics/ShipmentTracking'));
const PODManagement = lazy(() => import('./features/logistics/PODManagement'));
const EarningsDashboard = lazy(() => import('./features/logistics/EarningsDashboard'));
const LogisticsPartnerOnboarding = lazy(() => import('./features/logistics/LogisticsPartnerOnboarding'));
const LogisticsPartnerManagement = lazy(() => import('./features/logistics/LogisticsPartnerManagement'));
const LogisticsLifecycle = lazy(() => import('./features/logistics/LogisticsLifecycle'));

// Finance & Settlements
const SettlementDashboard = lazy(() => import('./features/finance/SettlementDashboard'));
const AccountsReceivable = lazy(() => import('./features/finance/AccountsReceivable'));
const AccountsPayable = lazy(() => import('./features/finance/AccountsPayable'));
const SettlementEngine = lazy(() => import('./features/finance/SettlementEngine'));
const EscrowManagement = lazy(() => import('./features/finance/EscrowManagement'));
const LedgerJournal = lazy(() => import('./features/finance/LedgerJournal'));
const BankReconciliation = lazy(() => import('./features/finance/BankReconciliation'));
const TDSTracking = lazy(() => import('./features/finance/TDSTracking'));

// Analytics
const PlatformAnalytics = lazy(() => import('./features/analytics/PlatformAnalytics'));

// Settings
const UserManagement = lazy(() => import('./features/settings/UserManagement'));
const KYCQueue = lazy(() => import('./features/settings/kyc-queue/KYCQueue'));
const ModerationQueue = lazy(() => import('./features/moderation/ModerationQueue'));
const DisputeSLABoard = lazy(() => import('./features/disputes-sla/DisputeSLABoard'));
const PricingGovernance = lazy(() => import('./features/pricing-governance/PricingGovernance'));
const PublicPricingEditor = lazy(() => import('./features/settings/PublicPricingEditor'));
const RentIndexEditor = lazy(() => import('./features/settings/RentIndexEditor'));
const PlatformConfig = lazy(() => import('./features/settings/PlatformConfig'));
const ListingFieldsConfig = lazy(() => import('./features/settings/ListingFieldsConfig'));
const AuditLogs = lazy(() => import('./features/settings/AuditLogs'));
const SLAManagement = lazy(() => import('./features/settings/sla'));
const MasterDataManagement = lazy(() => import('./features/settings/MasterDataManagement'));
const BrandingConfig = lazy(() => import('./features/settings/BrandingConfig'));
const IntegrationsConfig = lazy(() => import('./features/settings/integrations-cfg'));
const BusinessRulesConfig = lazy(() => import('./features/settings/BusinessRulesConfig'));
const NotificationTemplatesConfig = lazy(() => import('./features/settings/NotificationTemplatesConfig'));
const DocumentTemplates = lazy(() => import('./features/settings/DocumentTemplates'));
const ProviderTemplateConfig = lazy(() => import('./features/settings/ProviderTemplateConfig'));
const FeatureFlags = lazy(() => import('./features/settings/FeatureFlags'));
const MenuVisibility = lazy(() => import('./features/settings/MenuVisibility'));
const RolePermissions = lazy(() => import('./features/settings/RolePermissions'));
const TenantManagement = lazy(() => import('./features/settings/TenantManagement'));

const LandingPageManager = lazy(() => import('./features/settings/LandingPageManager'));
const LeadsEnquiries = lazy(() => import('./features/settings/LeadsEnquiries'));
const DPDPCompliance = lazy(() => import('./features/settings/DPDPCompliance'));
const HelpCenter = lazy(() => import('./features/support/HelpCenter'));
const FAQManagement = lazy(() => import('./features/support/FAQManagement'));
const ContractClauseConfig = lazy(() => import('./features/settings/ContractClauseConfig'));
// Host-scoped contract defaults — the "my numbers" layer the host
// controls (late fees, termination notice, custom clauses). Sits
// alongside the admin clause-config page.
const HostContractDefaults = lazy(() => import('./features/settings/HostContractDefaults'));
// Phase-2a contract template composer — narrative section authoring
// with variable placeholders. Hosts fork a platform starter, edit
// unlocked sections, preview PDF, set as default for their new
// bookings.
const ContractTemplatesList = lazy(() => import('./features/settings/ContractTemplatesList'));
const ContractTemplateComposer = lazy(() => import('./features/settings/ContractTemplateComposer'));
// Admin-only queue for host templates waiting on legal review.
const ContractReviewQueue = lazy(() => import('./features/settings/ContractReviewQueue'));
const TeamMembers = lazy(() => import('./features/settings/TeamMembers'));
const SupportTickets = lazy(() => import('./features/settings/SupportTickets'));

// Profile
const UserProfile = lazy(() => import('./features/profile/UserProfile'));

// My Organization (self-service)
const MyStorage = lazy(() => import('./features/myorg/MyStorage'));
const MyOrganization = lazy(() => import('./features/myorg/MyOrganization'));
const MyTeam = lazy(() => import('./features/myorg/team'));
const MyBilling = lazy(() => import('./features/myorg/MyBilling'));
const MyWarehouses = lazy(() => import('./features/myorg/MyWarehouses'));
const MyPreferences = lazy(() => import('./features/myorg/MyPreferences'));

// Tenant Management (top-level)
const TenantList = lazy(() => import('./features/tenants/TenantList'));
const TenantDetail = lazy(() => import('./features/tenants/TenantDetail'));
const WebhookManagement = lazy(() => import('./features/settings/WebhookManagement'));
const NotificationDashboard = lazy(() => import('./features/settings/NotificationDashboard'));
const WorkflowEngine = lazy(() => import('./features/settings/WorkflowEngine'));
const ComplianceTools = lazy(() => import('./features/settings/ComplianceTools'));

// E-Commerce & Fulfillment
const ECommerceConnectors = lazy(() => import('./features/ecommerce/ECommerceConnectors'));
const OrderSyncDashboard = lazy(() => import('./features/ecommerce/OrderSyncDashboard'));
const FulfillmentCenter = lazy(() => import('./features/ecommerce/FulfillmentCenter'));
const ScheduledReports = lazy(() => import('./features/settings/scheduled-reports'));
const AnomalyAlerts = lazy(() => import('./features/settings/AnomalyAlerts'));

// Compliance & GST
// Phase 14j — real-data EWB page replaces the mock version that
// previously lived in ``features/compliance/``.
const EwayBillManagement = lazy(() => import('./features/billing/EwayBillManagement'));
const EwayBillTemplates = lazy(() => import('./features/settings/EwayBillTemplates'));
const HSNSACManagement = lazy(() => import('./features/compliance/HSNSACManagement'));
const GSTFilingHelper = lazy(() => import('./features/compliance/GSTFilingHelper'));

// Procurement
const VendorManagement = lazy(() => import('./features/procurement/VendorManagement'));
const ProductManagement = lazy(() => import('./features/procurement/ProductManagement'));
const PurchaseOrderManagement = lazy(() => import('./features/procurement/PurchaseOrderManagement'));

// Inventory (additional)
const CycleCountManagement = lazy(() => import('./features/inventory/CycleCountManagement'));
const StockAlerts = lazy(() => import('./features/inventory/StockAlerts'));
const BarcodeManager = lazy(() => import('./features/inventory/BarcodeManager'));
const InterWarehouseTransfer = lazy(() => import('./features/inventory/InterWarehouseTransfer'));
const BatchLotTracking = lazy(() => import('./features/inventory/BatchLotTracking'));
const KitBundleManagement = lazy(() => import('./features/inventory/KitBundleManagement'));

// Analytics (additional)
const ABCXYZAnalysis = lazy(() => import('./features/analytics/ABCXYZAnalysis'));
const ReportsBuilder = lazy(() => import('./features/analytics/ReportsBuilder'));
const HostAnalytics = lazy(() => import('./features/analytics/HostAnalytics'));

// Settings (additional)
const DataImportExport = lazy(() => import('./features/settings/DataImportExport'));
const SMSWhatsAppConfig = lazy(() => import('./features/settings/SMSWhatsAppConfig'));
const PaymentGatewayConfig = lazy(() => import('./features/settings/PaymentGatewayConfig'));
const ThemeSettings = lazy(() => import('./features/settings/theme'));
const APIDocumentation = lazy(() => import('./features/settings/APIDocumentation'));
const ChangelogPage = lazy(() => import('./features/settings/ChangelogPage'));

// Integrations (ONDC + API Gateway)
const ONDCIntegration = lazy(() => import('./features/integrations/ONDCIntegration'));
const ExternalAPIGateway = lazy(() => import('./features/integrations/ExternalAPIGateway'));

// Onboarding
const OnboardingWizard = lazy(() => import('./features/onboarding/OnboardingWizard'));
const KycOnboardingWizard = lazy(() => import('./features/onboarding/KycOnboardingWizard'));

// Dashboard (additional)
const DashboardCustomizer = lazy(() => import('./features/dashboard/DashboardCustomizer'));

const ALL_ROLES = Object.values(ROLES);
const { ADMIN, HOST, TENANT, STAFF, LOGISTICS_PARTNER } = ROLES;

const SuspenseFallback = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
    <LoadingSpinner text="Loading page..." />
  </div>
);

const App = () => {
  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { fontSize: '0.875rem' },
        }}
      />
      <RouteTracker />
      <ErrorBoundary>
      <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          {/* Public pages - accessible without login */}
          <Route path="/" element={<SearchPage />} />
          <Route path="/find-warehouse" element={<FindWarehouseWizard />} />
          <Route path="/cost-calculator" element={<CostCalculator />} />
          <Route path="/warehouse-rent-index" element={<WarehouseRentIndex />} />
          <Route path="/warehouse-space" element={<CityHub />} />
          <Route path="/warehouse-space/:city" element={<CityLanding />} />
          <Route path="/warehouse-space/:city/:type" element={<CityLanding />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/search/:id" element={<ListingDetailPublic />} />
          {/* Listing detail is public - content is masked until the visitor logs in. */}
          <Route path="/marketplace" element={<SearchPage />} />
          <Route path="/marketplace/:id" element={<ListingDetailPublic />} />
          <Route path="/hosts/:id" element={<HostProfilePage />} />
          {/* Public Help Center — no layout, for anonymous landing
              traffic / bookmarked URLs / future marketing links.
              Authenticated callers should link to ``/help-center``
              instead so the sidebar + header stay framed around it. */}
          <Route path="/help" element={<HelpCenter />} />

          {/* Public info / legal pages — linked from the marketing footer. */}
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/grievance" element={<GrievancePage />} />
          <Route path="/account-deletion" element={<AccountDeletionPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/compliance/:slug" element={<CompliancePage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          {/* /demo is an alias of the contact page with the topic
              preset to "demo" so deep-link CTAs ("Book a 30-min demo")
              land on a form pre-tagged for the right routing. */}
          <Route path="/demo" element={<Navigate to="/contact?topic=demo" replace />} />

          {/* Public auth routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            {/* Consent gate — authenticated but interstitial. Lives in
                AuthLayout (centered card) because it's pre-app. */}
            <Route
              path="/consent"
              element={
                <ProtectedRoute>
                  <ConsentScreen />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* KYC onboarding wizard — gated only by auth + DPDP consent.
              Sits OUTSIDE the RequireKyc wrapper so the user can
              actually reach the wizard when they have unfinished KYC. */}
          <Route
            element={
              <ProtectedRoute>
                <RequireMandatoryConsent>
                  <WMSLayout />
                </RequireMandatoryConsent>
              </ProtectedRoute>
            }
          >
            <Route path="/kyc-onboarding" element={<KycOnboardingWizard />} />
          </Route>

          {/* Protected routes with main layout. RequireMandatoryConsent
              redirects to /consent if any mandatory DPDP consent is
              missing. RequireKyc redirects to /kyc-onboarding if the
              user's role demands KYC and any required item (PAN /
              Aadhaar / GSTIN / bank / role-specific docs) is missing. */}
          <Route
            element={
              <ProtectedRoute>
                <RequireMandatoryConsent>
                  <RequireKyc>
                    <WMSLayout />
                  </RequireKyc>
                </RequireMandatoryConsent>
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route path="/dashboard" element={<DashboardRouter />} />

            {/* Admin-shell wrapper around the public marketplace search.
                The bare ``/marketplace`` route below renders SearchPage
                outside any layout (right for tenants who land there
                from the sidebar). For admins clicking Marketplace ▸
                Search we want the admin header + sidebar to stay
                visible, so the same component renders inside
                ``WMSLayout`` at this path instead. */}
            <Route path="/admin/marketplace" element={<ProtectedRoute allowedRoles={[ADMIN]}><SearchPage /></ProtectedRoute>} />
            <Route path="/admin/marketplace/:id" element={<ProtectedRoute allowedRoles={[ADMIN]}><ListingDetailPublic /></ProtectedRoute>} />
            <Route path="/tenant/marketplace" element={<ProtectedRoute allowedRoles={[TENANT, HOST]}><SearchPage /></ProtectedRoute>} />
            <Route path="/tenant/marketplace/:id" element={<ProtectedRoute allowedRoles={[TENANT, HOST]}><ListingDetailPublic /></ProtectedRoute>} />

            {/* KYC */}
            <Route path="/kyc" element={<KYCUpload />} />

            {/* Warehouse management - specific paths BEFORE :id param */}
            <Route path="/warehouses/map" element={<ProtectedRoute allowedRoles={[ADMIN]}><WarehouseMap /></ProtectedRoute>} />
            <Route path="/warehouses" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ListingList /></ProtectedRoute>} />
            <Route path="/warehouses/new" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ListingWizard /></ProtectedRoute>} />
            <Route path="/warehouses/onboard" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><WarehouseOnboarding /></ProtectedRoute>} />
            <Route path="/warehouses/approvals" element={<ProtectedRoute allowedRoles={[ADMIN]}><ListingApproval /></ProtectedRoute>} />
            <Route path="/warehouses/:id/configure" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ShelfConfiguration /></ProtectedRoute>} />
            <Route path="/warehouses/:id/edit" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ListingWizard /></ProtectedRoute>} />
            <Route path="/warehouses/:warehouseId/listings/new" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ListingWizard /></ProtectedRoute>} />
            <Route path="/warehouses/:id/availability" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><AvailabilityCalendar /></ProtectedRoute>} />
            <Route path="/warehouses/:id" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ListingDetail /></ProtectedRoute>} />

            {/* Marketplace (primarily Tenant).
                Public landing/detail are handled above; only the gated flows
                (saved searches, booking, compare) stay protected. */}
            <Route path="/marketplace/saved" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><SavedSearches /></ProtectedRoute>} />
            <Route path="/marketplace/:id/book" element={<ProtectedRoute allowedRoles={[TENANT]}><BookingFlow /></ProtectedRoute>} />
            <Route path="/marketplace/compare" element={<ComparisonTool />} />

            {/* Bookings - specific paths BEFORE :id param */}
            <Route path="/bookings" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><BookingList /></ProtectedRoute>} />
            <Route path="/bookings/approvals" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><BookingApproval /></ProtectedRoute>} />
            {/* alias so hosts can find it easily */}
            <Route path="/bookings/:id/contract" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><ContractViewer /></ProtectedRoute>} />
            <Route path="/bookings/:id" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><BookingDetail /></ProtectedRoute>} />

            {/* Contracts */}
            <Route path="/contracts" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><ContractList /></ProtectedRoute>} />
            <Route path="/contracts/:id" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><ContractDetail /></ProtectedRoute>} />

            {/* Inventory */}
            <Route path="/inventory/returns" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT, STAFF]}><ReturnsManagement /></ProtectedRoute>} />
            <Route path="/inventory/damage-register" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><DamageLossRegister /></ProtectedRoute>} />
            {/* Phase 7b — tenants (stock owners) approve / reject
                damage claims against their own inventory. Admins
                can also hit this page to act on support escalations. */}
            <Route path="/inventory/damage-approvals" element={<ProtectedRoute allowedRoles={[TENANT, ADMIN]}><DamageApprovals /></ProtectedRoute>} />
            <Route path="/inventory/warehouse-layout" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><WarehouseLayout /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT, STAFF]}><InventoryList /></ProtectedRoute>} />
            <Route path="/inventory/grn" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT, STAFF]}><GRNList /></ProtectedRoute>} />
            <Route path="/inventory/grn/new" element={<ProtectedRoute allowedRoles={[TENANT, STAFF]}><GRNCreate /></ProtectedRoute>} />
            <Route path="/inventory/grn/:id" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT, STAFF]}><GRNDetail /></ProtectedRoute>} />
            <Route path="/inventory/grn/:id/qc" element={<ProtectedRoute allowedRoles={[STAFF]}><QCInspection /></ProtectedRoute>} />
            <Route path="/inventory/putaway" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><PutawayWorkbench /></ProtectedRoute>} />
            <Route path="/inventory/dispatch" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT, STAFF]}><DispatchList /></ProtectedRoute>} />
            <Route path="/inventory/dispatch/new" element={<ProtectedRoute allowedRoles={[TENANT, STAFF]}><DispatchCreate /></ProtectedRoute>} />
            <Route path="/inventory/cycle-count" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><CycleCountManagement /></ProtectedRoute>} />
            <Route path="/inventory/stock-alerts" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT, STAFF]}><StockAlerts /></ProtectedRoute>} />
            <Route path="/inventory/barcodes" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><BarcodeManager /></ProtectedRoute>} />
            <Route path="/inventory/transfers" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><InterWarehouseTransfer /></ProtectedRoute>} />
            <Route path="/inventory/batch-tracking" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT, STAFF]}><BatchLotTracking /></ProtectedRoute>} />
            <Route path="/inventory/kits" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><KitBundleManagement /></ProtectedRoute>} />

            {/* Procurement */}
            <Route path="/procurement/vendors" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><VendorManagement /></ProtectedRoute>} />
            <Route path="/procurement/products" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><ProductManagement /></ProtectedRoute>} />
            <Route path="/procurement/purchase-orders" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><PurchaseOrderManagement /></ProtectedRoute>} />

            {/* Compliance & GST */}
            <Route path="/compliance/eway-bills" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><EwayBillManagement /></ProtectedRoute>} />
            <Route path="/compliance/hsn-sac" element={<ProtectedRoute allowedRoles={[ADMIN]}><HSNSACManagement /></ProtectedRoute>} />
            <Route path="/compliance/gst-filing" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><GSTFilingHelper /></ProtectedRoute>} />

            {/* Billing */}
            <Route path="/billing/invoices" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><InvoiceList /></ProtectedRoute>} />
            <Route path="/billing/invoices/:id" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><InvoiceDetail /></ProtectedRoute>} />
            <Route path="/billing/payments" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><PaymentHistory /></ProtectedRoute>} />
            <Route path="/billing/rate-cards" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><RateCardManagement /></ProtectedRoute>} />
            <Route path="/billing/payouts" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><PayoutDashboard /></ProtectedRoute>} />
            {/* Booking-level payouts (Yes Bank / stub) distinct from invoice payouts above. */}
            <Route path="/finance/host-payouts" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><HostPayouts /></ProtectedRoute>} />
            <Route path="/finance/my-invoices" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><MyInvoices /></ProtectedRoute>} />
            <Route path="/finance/bank-accounts" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><HostBankAccounts /></ProtectedRoute>} />
            <Route path="/finance/reversals" element={<ProtectedRoute allowedRoles={[ADMIN]}><ReversalLog /></ProtectedRoute>} />
            <Route path="/billing/nach-mandates" element={<ProtectedRoute allowedRoles={[ADMIN, TENANT]}><NachMandates /></ProtectedRoute>} />
            <Route path="/billing/gst-report" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><GSTReport /></ProtectedRoute>} />
            {/* Phase 14o — section-grouped GSTR-3B (monthly) + GSTR-9
                (annual) + tax ledger + e-way bill management. All
                scoped by supplier GSTIN via the host's registrations. */}
            <Route path="/billing/gstr-3b" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><GSTR3BReport /></ProtectedRoute>} />
            <Route path="/billing/gstr-9" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><GSTR9Report /></ProtectedRoute>} />
            <Route path="/billing/tax-ledger" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><TaxLedger /></ProtectedRoute>} />
            <Route path="/billing/eway-bills" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><EwayBillManagement /></ProtectedRoute>} />
            <Route path="/settings/eway-bill-templates" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><EwayBillTemplates /></ProtectedRoute>} />
            <Route path="/billing/credit-limits" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><CreditLimitManagement /></ProtectedRoute>} />
            {/* Phase 14r — inward ITC surfaces. Vendors + purchase
                invoices feed the ITC claim flow; GSTR-2B reconciliation
                is the Rule 36(4) compliance gate before each claim. */}
            <Route path="/billing/vendors" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><VendorMaster /></ProtectedRoute>} />
            <Route path="/billing/purchase-invoices" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><PurchaseInvoices /></ProtectedRoute>} />
            <Route path="/billing/gstr-2b" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><Gstr2bReconciliation /></ProtectedRoute>} />
            {/* Phase 14v — compliance ops dashboard. Read-only, links
                to source pages for every action item. */}
            <Route path="/billing/compliance-calendar" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ComplianceCalendar /></ProtectedRoute>} />
            {/* Phase 16 — settlement admin surfaces. */}
            <Route path="/finance/rate-cards" element={<ProtectedRoute allowedRoles={[ADMIN]}><CommissionRateCards /></ProtectedRoute>} />
            <Route path="/finance/settlement-settings" element={<ProtectedRoute allowedRoles={[ADMIN]}><SettlementSettings /></ProtectedRoute>} />
            <Route path="/finance/clawbacks" element={<ProtectedRoute allowedRoles={[ADMIN]}><ClawbackLog /></ProtectedRoute>} />
            <Route path="/finance/rolling-reserve" element={<ProtectedRoute allowedRoles={[ADMIN]}><RollingReserveLog /></ProtectedRoute>} />
            <Route path="/finance/settlement-statement" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><HostSettlementStatement /></ProtectedRoute>} />
            <Route path="/finance/gstr-8" element={<ProtectedRoute allowedRoles={[ADMIN]}><Gstr8Report /></ProtectedRoute>} />

            {/* Finance & Settlements */}
            <Route path="/finance" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><SettlementDashboard /></ProtectedRoute>} />
            <Route path="/finance/receivables" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><AccountsReceivable /></ProtectedRoute>} />
            <Route path="/finance/payables" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><AccountsPayable /></ProtectedRoute>} />
            <Route path="/finance/settlements" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><SettlementEngine /></ProtectedRoute>} />
            <Route path="/finance/escrow" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><EscrowManagement /></ProtectedRoute>} />
            <Route path="/finance/ledger" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><LedgerJournal /></ProtectedRoute>} />
            <Route path="/finance/bank-reconciliation" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><BankReconciliation /></ProtectedRoute>} />
            <Route path="/finance/tds" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><TDSTracking /></ProtectedRoute>} />

            {/* Spaces */}
            <Route path="/spaces" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><SpaceOverview /></ProtectedRoute>} />
            <Route path="/spaces/zones" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><ZoneManagement /></ProtectedRoute>} />
            <Route path="/spaces/racks" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><RackManagement /></ProtectedRoute>} />
            <Route path="/spaces/allocations" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><AllocationList /></ProtectedRoute>} />

            {/* Operations */}
            <Route path="/operations/tasks" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><TaskBoard /></ProtectedRoute>} />
            <Route path="/operations/tasks/:id" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><TaskDetail /></ProtectedRoute>} />
            <Route path="/operations/dock-schedule" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><DockSchedule /></ProtectedRoute>} />
            <Route path="/operations/overstay" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><OverstayManagement /></ProtectedRoute>} />
            <Route path="/operations/tracking" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><InboundOutboundTracker /></ProtectedRoute>} />
            <Route path="/operations/packaging" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><PackagingManagement /></ProtectedRoute>} />
            <Route path="/operations/workforce" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><WorkforceManagement /></ProtectedRoute>} />
            <Route path="/operations/team-chat" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF]}><TeamChat /></ProtectedRoute>} />

            {/* Notifications */}
            <Route path="/notifications" element={<NotificationCenter />} />
            <Route path="/notifications/preferences" element={<NotificationPreferences />} />
            <Route path="/notifications/templates" element={<ProtectedRoute allowedRoles={[ROLES.ADMIN]}><NotificationTemplates /></ProtectedRoute>} />

            {/* Reviews */}
            <Route path="/reviews/trust-management" element={<ProtectedRoute allowedRoles={[ADMIN]}><TrustScoreManagement /></ProtectedRoute>} />
            <Route path="/reviews" element={<ProtectedRoute allowedRoles={[ADMIN]}><ReviewList /></ProtectedRoute>} />
            <Route path="/reviews/new/:bookingId" element={<ProtectedRoute allowedRoles={[TENANT]}><ReviewForm /></ProtectedRoute>} />
            <Route path="/reviews/trust-score/:userId" element={<TrustScoreCard />} />
            {/* Host-scoped cockpit pages — Phase 19a */}
            <Route path="/my-reviews" element={<ProtectedRoute allowedRoles={[HOST]}><MyReviews /></ProtectedRoute>} />
            <Route path="/my-trust-score" element={<ProtectedRoute allowedRoles={[HOST]}><MyTrustScore /></ProtectedRoute>} />

            {/* Disputes */}
            <Route path="/disputes" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><DisputeList /></ProtectedRoute>} />
            <Route path="/disputes/new" element={<ProtectedRoute allowedRoles={[HOST, TENANT]}><DisputeCreate /></ProtectedRoute>} />
            <Route path="/disputes/:id" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><DisputeDetail /></ProtectedRoute>} />

            {/* Analytics */}
            <Route path="/analytics" element={<ProtectedRoute allowedRoles={[ADMIN]}><PlatformAnalytics /></ProtectedRoute>} />
            <Route path="/analytics/:tab" element={<ProtectedRoute allowedRoles={[ADMIN]}><PlatformAnalytics /></ProtectedRoute>} />
            <Route path="/analytics/abc-xyz" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ABCXYZAnalysis /></ProtectedRoute>} />
            <Route path="/analytics/reports" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ReportsBuilder /></ProtectedRoute>} />
            {/* Host-scoped analytics — period-filtered (All / Year /
                Month / Range). Distinct from the admin Platform
                Analytics tabs above; backed by
                /host-analytics/me/aggregate. */}
            <Route path="/analytics/host" element={<ProtectedRoute allowedRoles={[HOST, ADMIN]}><HostAnalytics /></ProtectedRoute>} />

            {/* E-Commerce & Fulfillment */}
            <Route path="/ecommerce/connectors" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ECommerceConnectors /></ProtectedRoute>} />
            <Route path="/ecommerce/order-sync" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><OrderSyncDashboard /></ProtectedRoute>} />
            <Route path="/ecommerce/fulfillment" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, STAFF, TENANT, LOGISTICS_PARTNER]}><FulfillmentCenter /></ProtectedRoute>} />

            {/* Logistics - specific paths before :id params */}
            <Route path="/logistics/lifecycle" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT, LOGISTICS_PARTNER]}><LogisticsLifecycle /></ProtectedRoute>} />
            <Route path="/logistics/partner-onboarding" element={<ProtectedRoute allowedRoles={[ADMIN, LOGISTICS_PARTNER]}><LogisticsPartnerOnboarding /></ProtectedRoute>} />
            <Route path="/logistics/partner-management" element={<ProtectedRoute allowedRoles={[ADMIN]}><LogisticsPartnerManagement /></ProtectedRoute>} />
            <Route path="/logistics/freight-requests/new" element={<ProtectedRoute allowedRoles={[ADMIN, TENANT]}><FreightRequestCreate /></ProtectedRoute>} />
            <Route path="/logistics/freight-requests" element={<ProtectedRoute allowedRoles={[ADMIN, TENANT, LOGISTICS_PARTNER]}><FreightRequestList /></ProtectedRoute>} />
            <Route path="/logistics/shipments" element={<ProtectedRoute allowedRoles={[ADMIN, TENANT, LOGISTICS_PARTNER]}><ShipmentTracking /></ProtectedRoute>} />
            <Route path="/logistics/shipments/:id" element={<ProtectedRoute allowedRoles={[ADMIN, TENANT, LOGISTICS_PARTNER]}><ShipmentTracking /></ProtectedRoute>} />
            <Route path="/logistics/pod/:shipmentId" element={<ProtectedRoute allowedRoles={[ADMIN, LOGISTICS_PARTNER, TENANT]}><PODManagement /></ProtectedRoute>} />
            <Route path="/logistics/earnings" element={<ProtectedRoute allowedRoles={[ADMIN, LOGISTICS_PARTNER]}><EarningsDashboard /></ProtectedRoute>} />

            {/* Tenant Management (top-level) */}
            <Route path="/tenants" element={<ProtectedRoute allowedRoles={[ADMIN]}><TenantList /></ProtectedRoute>} />
            <Route path="/tenants/:id" element={<ProtectedRoute allowedRoles={[ADMIN]}><TenantDetail /></ProtectedRoute>} />

            {/* Profile (all authenticated users) */}
            <Route path="/profile" element={<UserProfile />} />
            {/* In-app Help Center — same component as the public
                ``/help`` route but rendered inside WMSLayout so the
                sidebar + header stay visible. Sidebar / footer /
                support widget all link here for authenticated users. */}
            <Route path="/help-center" element={<HelpCenter />} />

            {/* My Organization (self-service portal) */}
            <Route path="/my-storage" element={<ProtectedRoute allowedRoles={[ADMIN, TENANT]}><MyStorage /></ProtectedRoute>} />
            {/* Phase 21 — tenants' legacy dashboard (KPIs + bookings +
               inventory) lives here. /dashboard for TENANT now renders
               the marketplace search; this preserves the old view at
               a distinct URL surfaced as "My Dashboard" in the sidebar. */}
            <Route path="/my-dashboard" element={<ProtectedRoute allowedRoles={[TENANT]}><MyStorage /></ProtectedRoute>} />
            <Route path="/my-org" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><MyOrganization /></ProtectedRoute>} />
            <Route path="/my-org/team" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><MyTeam /></ProtectedRoute>} />
            <Route path="/my-billing" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><MyBilling /></ProtectedRoute>} />
            <Route path="/my-warehouses" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><MyWarehouses /></ProtectedRoute>} />
            <Route path="/my-preferences" element={<MyPreferences />} />

            {/* Settings */}
            <Route path="/settings/users" element={<ProtectedRoute allowedRoles={[ADMIN]}><UserManagement /></ProtectedRoute>} />
            <Route path="/settings/kyc-queue" element={<ProtectedRoute allowedRoles={[ADMIN]}><KYCQueue /></ProtectedRoute>} />
            <Route path="/settings/moderation" element={<ProtectedRoute allowedRoles={[ADMIN]}><ModerationQueue /></ProtectedRoute>} />
            <Route path="/disputes/sla-board" element={<ProtectedRoute allowedRoles={[ADMIN]}><DisputeSLABoard /></ProtectedRoute>} />
            <Route path="/settings/pricing" element={<ProtectedRoute allowedRoles={[ADMIN]}><PricingGovernance /></ProtectedRoute>} />
            <Route path="/settings/public-pricing" element={<ProtectedRoute allowedRoles={[ADMIN]}><PublicPricingEditor /></ProtectedRoute>} />
            <Route path="/settings/rent-index" element={<ProtectedRoute allowedRoles={[ADMIN]}><RentIndexEditor /></ProtectedRoute>} />
            <Route path="/settings/platform" element={<ProtectedRoute allowedRoles={[ADMIN]}><PlatformConfig /></ProtectedRoute>} />
            <Route path="/settings/audit-logs" element={<ProtectedRoute allowedRoles={[ADMIN]}><AuditLogs /></ProtectedRoute>} />
            <Route path="/settings/sla" element={<ProtectedRoute allowedRoles={[ADMIN]}><SLAManagement /></ProtectedRoute>} />
            <Route path="/settings/master-data" element={<ProtectedRoute allowedRoles={[ADMIN]}><MasterDataManagement /></ProtectedRoute>} />
            <Route path="/settings/branding" element={<ProtectedRoute allowedRoles={[ADMIN]}><BrandingConfig /></ProtectedRoute>} />
            <Route path="/settings/integrations" element={<ProtectedRoute allowedRoles={[ADMIN]}><IntegrationsConfig /></ProtectedRoute>} />
            <Route path="/settings/business-rules" element={<ProtectedRoute allowedRoles={[ADMIN]}><BusinessRulesConfig /></ProtectedRoute>} />
            <Route path="/settings/notification-templates" element={<ProtectedRoute allowedRoles={[ADMIN]}><NotificationTemplatesConfig /></ProtectedRoute>} />
            <Route path="/settings/document-templates" element={<ProtectedRoute allowedRoles={[ADMIN]}><DocumentTemplates /></ProtectedRoute>} />
            <Route path="/settings/provider-templates" element={<ProtectedRoute allowedRoles={[HOST]}><ProviderTemplateConfig /></ProtectedRoute>} />
            <Route path="/settings/feature-flags" element={<ProtectedRoute allowedRoles={[ADMIN]}><FeatureFlags /></ProtectedRoute>} />
            <Route path="/settings/menu-visibility" element={<ProtectedRoute allowedRoles={[ADMIN]}><MenuVisibility /></ProtectedRoute>} />
            <Route path="/settings/roles-permissions" element={<ProtectedRoute allowedRoles={[ADMIN]}><RolePermissions /></ProtectedRoute>} />
            <Route path="/settings/tenant-management" element={<ProtectedRoute allowedRoles={[ADMIN]}><TenantList /></ProtectedRoute>} />
            <Route path="/settings/webhooks" element={<ProtectedRoute allowedRoles={[ADMIN]}><WebhookManagement /></ProtectedRoute>} />
            <Route path="/settings/notification-dashboard" element={<ProtectedRoute allowedRoles={[ADMIN]}><NotificationDashboard /></ProtectedRoute>} />
            <Route path="/settings/workflow-engine" element={<ProtectedRoute allowedRoles={[ADMIN]}><WorkflowEngine /></ProtectedRoute>} />
            <Route path="/settings/compliance-tools" element={<ProtectedRoute allowedRoles={[ADMIN]}><ComplianceTools /></ProtectedRoute>} />
            <Route path="/settings/scheduled-reports" element={<ProtectedRoute allowedRoles={[ADMIN]}><ScheduledReports /></ProtectedRoute>} />
            <Route path="/settings/anomaly-alerts" element={<ProtectedRoute allowedRoles={[ADMIN]}><AnomalyAlerts /></ProtectedRoute>} />
            <Route path="/settings/landing-page" element={<ProtectedRoute allowedRoles={[ADMIN]}><LandingPageManager /></ProtectedRoute>} />
            <Route path="/settings/enquiries" element={<ProtectedRoute allowedRoles={[ADMIN]}><LeadsEnquiries /></ProtectedRoute>} />
            <Route path="/settings/blog" element={<ProtectedRoute allowedRoles={[ADMIN]}><BlogManager /></ProtectedRoute>} />
            <Route path="/settings/listing-fields" element={<ProtectedRoute allowedRoles={[ADMIN]}><ListingFieldsConfig /></ProtectedRoute>} />
            <Route path="/settings/faq-management" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><FAQManagement /></ProtectedRoute>} />
            <Route path="/settings/dpdp-compliance" element={<ProtectedRoute allowedRoles={[ADMIN]}><DPDPCompliance /></ProtectedRoute>} />
            <Route path="/settings/contract-clauses" element={<ProtectedRoute allowedRoles={[ADMIN]}><ContractClauseConfig /></ProtectedRoute>} />
            <Route path="/settings/contract-defaults" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><HostContractDefaults /></ProtectedRoute>} />
            <Route path="/settings/contract-templates" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ContractTemplatesList /></ProtectedRoute>} />
            <Route path="/settings/contract-templates/:id" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><ContractTemplateComposer /></ProtectedRoute>} />
            <Route path="/settings/contract-review" element={<ProtectedRoute allowedRoles={[ADMIN]}><ContractReviewQueue /></ProtectedRoute>} />
            <Route path="/settings/team-members" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><TeamMembers /></ProtectedRoute>} />
            <Route path="/settings/fulfillment-roles" element={<ProtectedRoute allowedRoles={[ADMIN, HOST]}><FulfillmentRoles /></ProtectedRoute>} />
            <Route path="/settings/support-tickets" element={<ProtectedRoute allowedRoles={[ADMIN]}><SupportTickets /></ProtectedRoute>} />
            <Route path="/settings/data-import-export" element={<ProtectedRoute allowedRoles={[ADMIN]}><DataImportExport /></ProtectedRoute>} />
            <Route path="/settings/sms-whatsapp" element={<ProtectedRoute allowedRoles={[ADMIN]}><SMSWhatsAppConfig /></ProtectedRoute>} />
            <Route path="/settings/payment-gateway" element={<ProtectedRoute allowedRoles={[ADMIN]}><PaymentGatewayConfig /></ProtectedRoute>} />
            <Route path="/settings/theme" element={<ThemeSettings />} />
            <Route path="/settings/api-docs" element={<ProtectedRoute allowedRoles={[ADMIN, HOST, TENANT]}><APIDocumentation /></ProtectedRoute>} />
            <Route path="/settings/changelog" element={<ChangelogPage />} />

            {/* Integrations (ONDC + API Gateway) */}
            <Route path="/integrations/ondc" element={<ProtectedRoute allowedRoles={[ADMIN]}><ONDCIntegration /></ProtectedRoute>} />
            <Route path="/integrations/api-gateway" element={<ProtectedRoute allowedRoles={[ADMIN]}><ExternalAPIGateway /></ProtectedRoute>} />

            {/* Onboarding */}
            <Route path="/onboarding" element={<OnboardingWizard />} />

            {/* Dashboard Customizer */}
            <Route path="/dashboard/customize" element={<DashboardCustomizer />} />
          </Route>

          {/* Default catch-all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
      {/* Ask Kai — global, shows on every page incl. the public landing.
          Works anonymously (public-audience KB) and scopes to the user's
          role once signed in. */}
      <KaiWidget />
    </>
  );
};

// SPA page-view tracking for GA4. gtag is only defined once a real GA4
// Measurement ID is set in index.html; until then this is a no-op.
function RouteTracker() {
  const location = useLocation();
  // Markup-driven click events (data-track, tel:, wa.me, app stores).
  React.useEffect(() => { installGlobalClickTracking(); }, []);
  React.useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
      });
    }
  }, [location.pathname, location.search]);
  return null;
}

export default App;
